import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchAssignableMembersForDepartment, assignMemberToDepartment } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";
import BuildDepartmentDrawer from "../../components/build/BuildDepartmentDrawer";

const memberClassLabel = (cls, t) => {
  switch (cls) {
    case "Collaborator": return t("member.class.Collaborator");
    case "Specialist": return t("member.class.Specialist");
    case "Investor": return t("member.class.Investor");
    case "ProductUser": return t("member.class.ProductUser");
    case "CompanyMember": return t("member.class.CompanyMember");
    default: return cls || "";
  }
};

export default function BuildDepartmentMemberAssignPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAssignableMembersForDepartment(departmentId);
      setRows(
        (Array.isArray(data) ? data : []).map((m) => ({
          ...m,
          fullName: `${m.lastName || ""} ${m.firstName || ""}`.trim(),
          memberClassLabel: memberClassLabel(m.memberClass, t),
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssign = async (member) => {
    setAssigning(true);
    try {
      await assignMemberToDepartment(departmentId, COMPANY_ID, member);
      notify.success(t("message.assign.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    finally { setAssigning(false); }
  };

  const columns = [
    { field: "fullName", headerName: t("member.name"), flex: 1, minWidth: 160 },
    { field: "memberClassLabel", headerName: t("member.class"), flex: 1, minWidth: 130 },
    {
      field: "_actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          disabled={assigning}
          onClick={(e) => { e.stopPropagation(); handleAssign(params.row); }}
        >
          {t("member.assign")}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <BuildDepartmentDrawer />
          <Typography variant="h6">{t("member.assign.title")}</Typography>
        </Box>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.workerId ?? r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-row": { cursor: "default" },
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/members`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
